import { Box, Button, Grid, Group, Text, Textarea, TextInput } from '@mantine/core';
import { IconCopy, IconDeviceFloppy, IconInfoCircle, IconMail, IconMessage, IconTrash } from '@tabler/icons-react';
import DailyLimitSettings from '../DailyLimitSettings';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import getMergeTags from '../../constants/getMergeTags';
import Regex from '../../constants/Regex';
import useFetch from '../../helpers/useFetch';
import { validateHandlebarsValue } from '../../helpers/validateHandlebars';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import MergeTagsCollapsible from '../MergeTagsCollapsible';
import ConfirmationModal from '../Modals/ConfirmationModal';
import SendTestMessageModal from '../Modals/SendTestMessageModal';
import Spinner from '../Spinner';
import WarningMessageOnDelete from '../WarningMessageOnDelete';

const fetchMessagePreview = async (
  fetch,
  accountId,
  domainId,
  message,
  setLoading,
  setError,
  setMessagePreview,
  setMessageLengthInfo
) => {
  try {
    setLoading(true);
    const { shortenedMessage, messageLengthInfo } = await fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/shortenedUrlMessage`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      }
    );

    setMessagePreview(shortenedMessage);
    setMessageLengthInfo(messageLengthInfo);
  } catch (error) {
    setError(error.message);
  } finally {
    setLoading(false);
  }
};

const debouncedFetch = AwesomeDebouncePromise(fetchMessagePreview, 500);

const TextMessageForm = ({
  textMessage: passedTextMessage,
  saveTextMessage,
  deleteTextMessage,
  showUserFlow,
  deleteError,
  onBack,
}) => {
  const location = useLocation();
  const fetch = useFetch();
  const navigate = useNavigate();

  const [textMessage, setTextMessage] = useState();
  const [dynamicFieldsOpen, setDynamicFieldsOpen] = useState(false);
  const [sendTestMessageModalOpen, setSendTestMessageModalOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [customFields, setCustomFields] = useState();
  const [coupons, setCoupons] = useState();
  const [surveys, setSurveys] = useState([]);
  const [messagePreview, setMessagePreview] = useState('');
  const [messageLengthInfo, setMessageLengthInfo] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [loadError, setLoadError] = useState();
  const { user } = useSelector((state) => state.auth);
  const { domain } = useSelector((state) => state.domain);

  useEffect(() => {
    setTextMessage(passedTextMessage);
  }, [passedTextMessage]);

  useEffect(() => {
    const func = async () => {
      try {
        const { customFields } = await fetch(
          `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/customFields`
        );
        await setCustomFields(customFields);
      } catch (e) {
        setError(setLoadError);
      }
    };
    func();

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/coupons`)
      .then(({ coupons }) => setCoupons(coupons))
      .catch(setLoadError);

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/surveys`)
      .then(({ surveys }) => setSurveys(surveys))
      .catch(setLoadError);
  }, [domain.accountId, domain.id, fetch]);

  useEffect(() => {
    if (textMessage && textMessage.message) {
      debouncedFetch(
        fetch,
        domain.accountId,
        domain.id,
        textMessage.message,
        setLoading,
        setError,
        setMessagePreview,
        setMessageLengthInfo
      );
    }
  }, [fetch, domain.accountId, domain.id, textMessage]);

  const send = (sendTo, impersonateProfileQuery, triggerEventAction, cb) => {
    setError(undefined);
    setSending(true);
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/textMessages/testTextMessage`, {
      method: 'POST',
      body: JSON.stringify({ impersonateProfileQuery, sendTo, triggerEventAction, textMessage }),
    })
      .then(cb)
      .catch((error) => {
        setError(error);
      })
      .finally(() => setSending(false));
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!customFields || !textMessage) {
    return <Spinner />;
  }

  const messageHandlebarsError = validateHandlebarsValue(textMessage.message);

  const deleteButton = deleteTextMessage ? (
    <LockableButton restrictionKey="textMessagesDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
      <IconTrash size={14} /> Delete
    </LockableButton>
  ) : undefined;

  let duplicateButton;
  if (textMessage.id) {
    duplicateButton = (
      <LockableButton
        restrictionKey="textMessagesCreate"
        color="cyan"
        onClick={() => {
          navigate(
            location.pathname.replace(
              `textMessages/${textMessage.id}/details`,
              `textMessages/new?from=${textMessage.id}`
            )
          );
        }}
      >
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    );
  }

  return (
    <>
      <SendTestMessageModal
        send={send}
        close={() => setSendTestMessageModalOpen(false)}
        error={error}
        sending={sending}
        show={sendTestMessageModalOpen}
        sendToPlaceholder="Paste phone numbers (with +<countryCode>) separated by ','"
        sendTo={user.phoneNumber}
        isSendToValid={(sendTo) => sendTo.split(',').every((v) => Regex.phoneNumberRegex.test(v))}
      />
      <Grid>
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Name
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <TextInput
            name="name"
            type="text"
            value={textMessage.name}
            placeholder="e.g. Check This Out!"
            onChange={(e) => setTextMessage({ ...textMessage, [e.target.name]: e.target.value })}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Message
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <Group justify="flex-end" gap="xs" py="md">
            <Button
              size="sm"
              variant="default"
              onClick={() => setDynamicFieldsOpen(!dynamicFieldsOpen)}
              aria-expanded={dynamicFieldsOpen}
            >
              <IconInfoCircle size={14} /> Merge Tags
            </Button>
            <Button
              color="cyan"
              size="sm"
              disabled={!textMessage.message || Boolean(messageHandlebarsError)}
              onClick={() => setSendTestMessageModalOpen(true)}
            >
              <IconMessage size={14} /> Test Message
            </Button>
          </Group>
          <MergeTagsCollapsible open={dynamicFieldsOpen} mergeTags={getMergeTags(customFields, coupons, surveys)} />
          <Textarea
            rows={5}
            onChange={(e) => setTextMessage({ ...textMessage, message: e.target.value })}
            name="message"
            value={textMessage.message}
            error={messageHandlebarsError}
          />
          <Text size="sm" c="dimmed">
            <div>
              Length: {messageLengthInfo?.length ? messageLengthInfo?.length : textMessage.message?.length || 0} / 160
            </div>
            <div>
              We support the long message format so texts that go over the limit can be visualized as a single text
              message.
            </div>
            <div>
              Please note that links will be shortened. Actual link is provided in the <strong>Preview</strong> section
              down below.
            </div>
            <div>
              If the message includes merge tags like{' '}
              <strong>
                <code className="merge-tags">&#123;&#123;firstName&#125;&#125;</code>
              </strong>{' '}
              it might exceed the character limit and may require sending in multiple messages. Example: John (4 chars)
              Alexander (9 chars).
            </div>
          </Text>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Preview
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          {loading ? (
            <Spinner />
          ) : (
            <Textarea
              disabled
              rows={5}
              name="message"
              value={messagePreview ? messagePreview : 'No message preview available'}
            />
          )}
        </Grid.Col>
      </Grid>
      <DailyLimitSettings
        model={textMessage}
        onChange={(key, value) => setTextMessage({ ...textMessage, [key]: value })}
        channelLabel="text messages"
        maxPerDay={domain.maxTextMessagesPerDay}
        accountId={domain.accountId}
        domainId={domain.id}
        settingsTab="textMessage"
      />
      {error && <ErrorMessage message={error.message} />}
      <Group justify="flex-end" gap="xs" py="md">
        <Button variant="default" onClick={onBack}>
          {' '}
          Back
        </Button>
        {deleteButton}
        {duplicateButton}
        {showUserFlow && (
          <Button variant="default" onClick={() => showUserFlow()}>
            <IconMail size={14} /> Send
          </Button>
        )}
        <LockableButton
          restrictionKey={`textMessages${textMessage.id ? 'Update' : 'Create'}`}
          onClick={() => saveTextMessage(textMessage)}
          disabled={!textMessage.message || !textMessage.name || Boolean(messageHandlebarsError)}
        >
          <IconDeviceFloppy size={14} /> Save
        </LockableButton>
      </Group>
      <ConfirmationModal
        show={deleteError ? false : confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={(cb) => deleteTextMessage(cb)}
        message={<WarningMessageOnDelete type="Text Message" />}
      />
      {deleteError ? (
        <Box ta="right">
          <ErrorMessage message={deleteError} />
        </Box>
      ) : null}
    </>
  );
};

export default TextMessageForm;
